// Mirror of Shared/Calculators/SolarCalculator.cs.
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "monthlyKwh", kind: "number", default: 900, min: 50, max: 10000 },
  { key: "sunHoursPerDay", kind: "number", default: 4.5, min: 2, max: 7 },
  { key: "panelWatts", kind: "number", default: 400, min: 250, max: 700 },
  { key: "lossFactor", kind: "number", default: 0.8, min: 0.6, max: 0.95 },
  { key: "pricePerWatt", kind: "number", default: 3, min: 0, max: 10 },
];

function compute(i: Record<string, number>): Record<string, number> {
  const monthlyKwh = F.get(i, "monthlyKwh");
  const sunHours = F.get(i, "sunHoursPerDay");
  const panelWatts = F.get(i, "panelWatts");
  const loss = F.get(i, "lossFactor");
  const price = F.get(i, "pricePerWatt");

  const dailyKwh = monthlyKwh / 30;
  const denom = sunHours * loss;
  const systemKw = denom > 0 ? dailyKwh / denom : 0;
  const systemWatts = systemKw * 1000;
  const panels = panelWatts > 0 ? F.ceilUnits(systemWatts / panelWatts) : 0;
  const roofArea = panels * 18;
  const cost = systemWatts * price;

  return {
    dailyKwh: F.round(dailyKwh, 2),
    systemKw: F.round(systemKw, 2),
    systemWatts: F.round(systemWatts, 2),
    panels,
    roofArea: F.round(roofArea, 2),
    cost: F.round(cost, 2),
  };
}

function present(_i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  const panels = F.get(r, "panels");
  return [
    { label: "System size", value: `${F.dec(F.get(r, "systemKw"))} kW` },
    { label: "Panels needed", value: `${F.intStr(panels)} ${F.plural(panels, "panel", "panels")}`, highlight: true },
    { label: "Roof area", value: `~${F.intStr(F.get(r, "roofArea"))} sq ft` },
    { label: "Est. installed cost", value: F.usd(F.get(r, "cost")), sub: "rough — before tax credits and incentives; get local quotes" },
  ];
}

export const solar: CalcDef = { slug: "solar-calculator", fields, compute, present };
